/**
 * Every company the development brain has looked at: the Arendusaju tab of
 * Kliendiotsing.
 *
 * WHY THIS LIST EXISTS. Until R7 the brain had no address of its own. It was
 * reachable only through somebody else's list - a badge in scan-leads, a badge
 * in the register, a follow-up queue that shows a row only once a date has been
 * put on it. A run that produced a verdict and no follow-up date appeared
 * nowhere at all, so the tool's own memory was unreadable, and there was no
 * route a notification could point at.
 *
 * WHAT IT IS NOT. It is not a run log. One row per domain, and a new run
 * overwrites the row it is for, so what can honestly be listed is COMPANIES
 * with their latest run. The wording says "looked at", never "runs".
 *
 * PAGING IS A CURSOR, NOT A PAGE NUMBER. The sort is newest activity first and
 * a run finishing while somebody reads page two would shift every offset under
 * them, silently repeating one row and hiding another.
 *
 * THE AUTOPILOT'S STRIP SITS ABOVE THE LIST, because this is the engine's own
 * tab: the switch and the rows it produces belong to the same question. It
 * lived on the scanner-leads page until R11 only because the brain had no page
 * of its own on the day the engine was built, one day before this one existed.
 * The leads list is still where the engine picks its TARGETS from, which is an
 * argument about where the input comes from, not about where a person looks for
 * the switch or for what it spent. One line since plan step 5: what it spent
 * and how it is set up are behind the strip's gear, so the toolbar and the
 * list start right under it instead of under a card of settings.
 *
 * EVERY FILTER IS A SERVER FILTER, and with a cursor there is no other option
 * that is honest. Narrowing the fifty rows already loaded would leave the count
 * above them describing a different set, and "load more" would fetch the next
 * fifty of the UNFILTERED order - so an operator paging through a filtered list
 * would walk past matches without ever seeing them.
 *
 * NO HEAD OF ITS OWN. The page's head carries the title and this tab's
 * sentence; the refresh that sat in this list's head is a round button at the
 * end of its toolbar, beside the count it re-reads.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Brain, Loader2, RefreshCw } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import {
  listBrainRuns,
  type BrainFitDecision,
  type BrainListRow,
  type BrainStatus,
  type BrainWorked,
} from "../../../lib/siteBrain";
import {
  Btn, EmptyState, FilterMenu, IconBtn, MenuField, PillTabs, SearchField, Toolbar, ToolbarGap, listCard,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { userCanPlatform } from "../../../lib/auth";
import { BrainAutopilotPanel } from "./BrainAutopilotPanel";
import { BrainRowItem } from "./BrainRowItem";
import { useBrainAutopilot } from "./useBrainAutopilot";
import { countStates } from "./lib";

/**
 * Company, state, when, marks.
 *
 * The company track carries its own 220px floor rather than `minmax(0,...)`,
 * for the reason the follow-up queue's does: at the card's scroll floor the
 * flexible tracks divide what is left, and a company column that falls under
 * 200px renders "sel-nadalal.ee" as "s.". A row that cannot say which company
 * it is has stopped being a row.
 *
 * The marks track is fixed at 132px: four 28px controls plus their gaps plus
 * the cell's right padding. A flex row with `justify-end` and no room does not
 * shrink and does not scroll - it overflows to the LEFT, over the column
 * beside it, and `scrollWidth` never reports it.
 */
const COLS = "minmax(220px,2.4fr) minmax(0,1.2fr) 150px 132px";

const PAGE = 50;

type Filter = "all" | BrainStatus;

/** The verdicts, in the order an operator reads them: best prospect first. */
const FITS: BrainFitDecision[] = ["go", "hold", "no"];

/** "any" is the absence of the filter, not a fourth thing the row can be. */
type WorkedChoice = "any" | BrainWorked;
const WORKED: WorkedChoice[] = ["any", "untouched", "called", "planned"];

export function BrainListPage({ user }: { user: StaffUser }) {
  const t = useT();

  const [rows, setRows] = useState<BrainListRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [fits, setFits] = useState<BrainFitDecision[]>([]);
  const [worked, setWorked] = useState<WorkedChoice>("any");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");

  /**
   * One clock for the whole render pass.
   *
   * Every row asks "how old is this", and letting each one call `new Date()`
   * means the list can disagree with itself about where the 30-day line falls.
   */
  const now = useRef(new Date()).current;

  /** The typed query, settled. Every keystroke would be a request otherwise. */
  useEffect(() => {
    const timer = setTimeout(() => setSearch(q), 250);
    return () => clearTimeout(timer);
  }, [q]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await listBrainRuns({
        limit: PAGE,
        status: filter === "all" ? null : filter,
        q: search,
        fit: fits,
        worked: worked === "any" ? null : worked,
      });
      setRows(res.rows);
      setTotal(res.total);
      setCursor(res.nextCursor);
      setError(false);
    } catch {
      setRows([]);
      setTotal(0);
      setCursor(null);
      setError(true);
    }
  }, [filter, search, fits, worked]);

  useEffect(() => {
    void load();
  }, [load]);

  /** The next page, appended. Never replaces what is already on screen. */
  async function loadMore() {
    if (!cursor || more) return;
    setMore(true);
    try {
      const res = await listBrainRuns({
        limit: PAGE,
        cursor,
        status: filter === "all" ? null : filter,
        q: search,
        fit: fits,
        worked: worked === "any" ? null : worked,
      });
      setRows((prev) => [...(prev ?? []), ...res.rows]);
      setCursor(res.nextCursor);
      setTotal(res.total);
    } catch {
      setCursor(null);
    } finally {
      setMore(false);
    }
  }

  // Off the rows already on screen, not off a second request: these numbers
  // annotate what is visible, and a count that disagreed with the list under it
  // would be worse than no count.
  const counts = countStates(rows ?? [], now);

  /**
   * Is anything narrowing the list?
   *
   * Drives the empty state, and that is not decoration. "The brain has not
   * looked at anything yet" and "nothing matches what you asked for" are
   * opposite facts, and showing the first one to somebody who has ticked "go"
   * plus "untouched" tells them the tool is empty when it is full.
   */
  // The list is what a finished batch changes, so the engine's poll pulls it in.
  const autopilot = useBrainAutopilot(load);
  const canManage = userCanPlatform(user, "manage:ariregister");

  const narrowed = filter !== "all" || fits.length > 0 || worked !== "any" || search.trim().length > 0;

  const tabs = [
    { value: "all" as const, label: t("brain.list.filter.all") },
    { value: "running" as const, label: t("brain.list.filter.running"), count: counts.running },
    { value: "done" as const, label: t("brain.list.filter.done") },
    { value: "error" as const, label: t("brain.list.filter.error"), count: counts.error },
  ];

  return (
    <PopHost>
      <BrainAutopilotPanel
        state={autopilot.state}
        onChanged={() => {
          void autopilot.refresh();
          void load();
        }}
        canManage={canManage}
        t={t}
      />

      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("brain.list.search")} />
        <PillTabs tabs={tabs} value={filter} onChange={setFilter} small />
        {/* Behind one pill each rather than two rows of chips: the toolbar is
            one screen row, and this page already spends one on the status
            tabs. No counts on the options - a count here would have to be a
            second request per verdict, and it would answer a different question
            from the one under it anyway. */}
        <FilterMenu
          label={t("brain.list.fitFilter")}
          items={FITS.map((f) => ({ value: f, label: t(`brain.fit.decision.${f}`) }))}
          selected={fits}
          onToggle={(v) =>
            setFits((prev) =>
              prev.includes(v as BrainFitDecision)
                ? prev.filter((x) => x !== v)
                : [...prev, v as BrainFitDecision],
            )
          }
          onClear={fits.length > 0 ? () => setFits([]) : undefined}
          clearLabel={t("common.clear")}
        />
        <MenuField
          value={worked}
          onChange={setWorked}
          options={WORKED.map((w) => ({ value: w, label: t(`brain.list.worked.${w}`) }))}
          label={t("brain.list.workedFilter")}
        />
        <ToolbarGap />
        <span className="crm-sub px-1.5">
          {rows === null ? t("common.loading") : t("brain.list.count", { n: total })}
        </span>
        {/* A circle, not a worded pill: this toolbar already spends its line on
            the search, the status pills and two menus, and a worded button
            here wrapped alone onto a second row on a laptop. */}
        <IconBtn onClick={() => void load()} disabled={rows === null} aria-label={t("common.refresh")} title={t("common.refresh")}>
          <RefreshCw className={rows === null ? "size-4 animate-spin" : "size-4"} />
        </IconBtn>
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Brain}
            title={t(error ? "brain.list.errorTitle" : narrowed ? "brain.list.noMatch" : "brain.list.empty")}
            body={t(error ? "brain.list.errorBody" : narrowed ? "brain.list.noMatchBody" : "brain.list.emptyBody")}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("brain.list.col.company")}</span>
              <span>{t("brain.list.col.state")}</span>
              <span>{t("brain.list.col.when")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((row) => (
                <BrainRowItem key={row.domain} row={row} cols={COLS} now={now} me={user.id} t={t} />
              ))}
            </div>

            {cursor ? (
              <div className="flex justify-center py-4">
                <Btn onClick={() => void loadMore()} disabled={more}>
                  {more ? <Loader2 className="size-4 animate-spin" /> : null}
                  {t("brain.list.more", { n: Math.max(0, total - rows.length) })}
                </Btn>
              </div>
            ) : null}
          </>
        )}
      </div>
    </PopHost>
  );
}
